import * as angular from 'angular';
import * as moment from 'moment';
import * as _ from 'lodash';
import {apsViewModels} from "../../shared/dto/APS.ViewModels";
import ChatMessage from "../../models/chat/chatMessage";
import {NavigationModel, Plant} from "../../models/navigation";
import {StateParams} from "../../common";
import DateUtilityService from "../../services/utility/dateutility.service";
import LoaderServiceName, {LoaderService} from "../../components/loader/loader.service";
import NavigationServiceName, {NavigationService} from "../../services/navigation/navigation.service";
import ShopServiceName, {ShopService} from "../../services/shops/shops.service";
import ChatServiceName, {ChatService} from "../../services/chat/chat.service";
import {States} from "../../shared/config/constant.module";
import template from "./chatReport.tpl.html";
import {ICommonQueryingReport} from "../reports/commonQueryingReport";

let requires = [LoaderServiceName, NavigationServiceName, ShopServiceName, ChatServiceName];
  
export interface ChatReportParams extends StateParams {
  reportPlantId: string;
  reportShopId: string;
  startDate: string;
  endDate: string;
  autoRun: boolean;
}
  
class ChatReportCtrl implements ICommonQueryingReport {
  plants: Plant[];
  selectedPlant: Plant;
  selectedShop: apsViewModels.ShopNavigation;
  startDate: Date;
  endDate: Date;
  uiStartDate: Date;
  uiEndDate: Date;

  form: angular.IFormController;

  requirePlantSelection: boolean;
  requireShopSelection: boolean;

  chatItems: ChatMessage[];

  shopConfig: apsViewModels.ShopConfigurationWithShifts;
  private shopService: ShopService;
  private chatService: ChatService;
  private loaderService: LoaderService;
  private $state: angular.ui.IStateService;
  private $q: angular.IQService;
  private $stateParams: ChatReportParams;

  constructor(
    chatService: ChatService,
    navigationService: NavigationService,
    loaderService: LoaderService,
    shopService: ShopService,
    $state: angular.ui.IStateService,
    $stateParams: ChatReportParams,
    $q: angular.IQService) {
    this.chatService = chatService;
    this.loaderService = loaderService;
    this.$state = $state;
    this.$stateParams = $stateParams;
    this.$q = $q;
    this.shopService = shopService;
    this.requirePlantSelection = true;
    this.requireShopSelection = true;

    let now = moment().add(1, 'hours').startOf('hour');
    this.uiEndDate = now.toDate();
    this.uiStartDate = now.clone().subtract(1, 'days').toDate();

    let promise = navigationService.getNavigation()
      .then((navModel: NavigationModel) => {
        this.plants = navModel.plants();
        this.selectedPlant = navModel.selectedPlant;
        this.updatePlant();
        this.selectedShop = navModel.selectedShop;

        return this.loadState($stateParams);
    });

    this.loaderService.loadingPromise(promise);
  }

  exportToCsv() { 

    return _.map<ChatMessage, any>(this.chatItems, (x) => {
      let type = ((x.isDefect) ? x.defectLabel() : 
                  ((x.isDowntime) ? 'Downtime' : 
                    ((x.isToolChange) ? 'Tool Change' : '')));

      return {
        createdOn: this.displayDate(x.createdOn),
        message: x.message,
        createdByFullName: x.createdByFullName,
        type: type,
        area: !x.isChat ? this.getLocation(x) : ''        
      };
    });
  }

  displayDate(date: Date): string {
    return DateUtilityService.formatTime(date, this.selectedPlant ? this.selectedPlant.timeZoneId : null);
  }

  getLocation(chatItem: ChatMessage): string {
    let location = chatItem.location(this.shopConfig);
    return location;
  }

  updatePlant(): void {
    this.selectedShop = null;
  }

  loadState(params: ChatReportParams): angular.IPromise<any> {
    if (params.reportPlantId && params.reportShopId && params.reportShopId !== params.shopId) {
      let plant = _.find(this.plants, {id: params.reportPlantId});
      if (plant) {
        let shop = _.find(plant.shops, {id: params.reportShopId});
        if (shop) {
          this.selectedPlant = plant;
          this.selectedShop = shop;
        }
      }
    }

    if (params.startDate) {
      let startDate = moment(params.startDate);
      if (startDate.isValid()) {
        this.startDate = startDate.toDate();
      }
    }

    if (params.endDate) {
      let endDate = moment(params.endDate);
      if (endDate.isValid()) {
        this.endDate = endDate.toDate();
      }
    }

    if (params.autoRun) {
      return this.runChatReport();
    }

    return this.$q.when();
  }

  saveState(): void {
    let reportShopId = this.selectedShop ? this.selectedShop.id : undefined;
    if (reportShopId === this.$stateParams.shopId) {
      reportShopId = undefined;
    }
    let params: ChatReportParams = {
      plantId: this.$stateParams.plantId,
      shopId: this.$stateParams.shopId,
      userId: this.$stateParams.userId,
      reportPlantId: (reportShopId && this.selectedPlant) ? this.selectedPlant.id : undefined,
      reportShopId: reportShopId,
      startDate: this.startDate ? this.startDate.toISOString() : undefined,
      endDate: this.endDate ? this.endDate.toISOString() : undefined,
      autoRun: true
    };

    this.$state.transitionTo(this.$state.current.name, params);
  }

  generateReport(): void {
    if (this.form.$invalid) {
      return;
    }

    this.saveState();

    this.loaderService.loadingPromise(this.runChatReport());
  }

  private runChatReport(): angular.IPromise<any> {
    this.endDate = this.endDate || new Date();
    this.chatItems = undefined;
    let shopPromise = this.shopService.getShopConfiguration(this.selectedShop.id)
      .then(shop => this.shopConfig = shop);

    let chatPromise = this.chatService.getMessages(
      this.selectedPlant.id, this.selectedShop.id, this.startDate, this.endDate)
      .then(data => this.chatItems = data);

    return this.$q.all([shopPromise, chatPromise]);
  }
}

export default angular.module('aps.controller.chatReport', requires)
  .config(($stateProvider: angular.ui.IStateProvider) => {
    $stateProvider.state({
      name: States.CHAT_REPORT,
      url: '/report/chat?startDate&endDate&autoRun&reportPlantId&reportShopId',
      params: {
        startDate: {dynamic: true},
        endDate: {dynamic: true},
        autoRun: {dynamic: true},
        reportPlantId: {dynamic: true},
        reportShopId: {dynamic: true},
      },
      controller: 'ChatReportCtrl as vm',
      template: template,
      data: {
        pageTitle: 'Chat and Comments Report'
      }
    });
  })
  .controller('ChatReportCtrl', ChatReportCtrl)
  .name;


// WEBPACK FOOTER //
// ./src/app/controllers/chat/chatReport.ts